import { create } from 'zustand';
import type { QueryClient } from '@tanstack/react-query';
import { queryClient } from '@/lib/query-client';
import { getAlerts } from './alerts.api';
import { isOperatorAlertV1, type IOperatorAlertV1 } from './alert.types';
import {
  byLead,
  deliveryOf,
  GROUP_MS,
  type AlertDelivery,
  type IDeliveryContext,
} from './delivery.policy';

// The console's operator-alert state (brief §7.5): what arrived, what is unread, the ONE
// evident dialog or drop (a burst grouped into it), the summary list, the away count the tab
// title carries. In memory and per tab — the durable inbox is the BFF's (§7.4, 24 h); the
// read cursor is the server's too (`POST /v1/alerts/read`), mirrored here as `readUpTo`.
//
// Live alerts come in through `receiveAlert` (WsEventBridge, lib/ws/ws-event-bridge.tsx);
// missed ones through `catchUp` — ONE `GET /v1/alerts?since=<last seen at>` per socket
// reconnect / return from away, never on a timer (frontend-conventions §8.1). Both run
// the same pure policy (`delivery.policy.ts`) and the same dedupe on `id` (bounded).

/** The list keeps the newest N (also the catch-up `limit`). */
export const LIST_CAP = 50;
/** Render-once memory: the last N alert ids, oldest first. */
export const SEEN_CAP = 200;

export interface IAlertEntry {
  alert: IOperatorAlertV1;
  unread: boolean;
}

export interface IAlertsState {
  /** Newest first, capped. */
  entries: IAlertEntry[];
  seen: string[];
  /** The newest `at` seen — the catch-up's `since`. */
  lastSeenAt: string;
  /** The local read cursor (ISO), the max of every acknowledgement seen. */
  readUpTo: string | null;
  /** The evident dialog: its alerts, lead first (`byLead`), and when it opened. */
  dialog: { alerts: IOperatorAlertV1[]; at: number } | null;
  /** The drop (operating): the same card, from the top of «Operar» — as the dialog. */
  drop: { alerts: IOperatorAlertV1[]; at: number } | null;
  /** The summary list: on return from away, or asked for (the bell / «Ver todas»). */
  summary: 'return' | 'list' | null;
  /** The last catch-up: in flight, failed, or fine. */
  sync: 'idle' | 'loading' | 'error';
  /** Alerts queued while away — the tab title's count and the summary's trigger. */
  awayCount: number;
  /** The alert the live region speaks (id). */
  said: string | null;
}

const fresh = (): IAlertsState => ({
  entries: [],
  seen: [],
  lastSeenAt: new Date().toISOString(),
  readUpTo: null,
  dialog: null,
  drop: null,
  summary: null,
  sync: 'idle',
  awayCount: 0,
  said: null,
});

export const useAlertsStore = create<IAlertsState>(fresh);

/** Tests and logout: back to an empty console. */
export function resetAlerts(): void {
  useAlertsStore.setState(fresh());
}

const atOf = (a: IOperatorAlertV1): number => Date.parse(a.at);
const later = (a: string | null, b: string): string =>
  a && Date.parse(a) >= Date.parse(b) ? a : b;

/** The newest `at` among these alerts — the read cursor an acknowledgement sends. */
export function upToOf(alerts: readonly IOperatorAlertV1[]): string | null {
  return alerts.reduce<string | null>((max, a) => later(max, a.at), null);
}

/**
 * A card's alerts with one more. Within the burst window the lead is re-ranked (`byLead`:
 * a cancellation before the disruption it opened, then the newest); after it, the lead
 * stays put (the operator is reading it) and the news only grows «y N más».
 */
function grown(
  card: { alerts: IOperatorAlertV1[]; at: number },
  alert: IOperatorAlertV1,
  now: number,
): IOperatorAlertV1[] {
  const alerts = [...card.alerts, alert];
  if (now - card.at <= GROUP_MS) alerts.sort(byLead);
  return alerts;
}

/** Apply ONE delivery decision. A repeated id is ignored, whatever the decision. */
export function takeAlert(
  alert: IOperatorAlertV1,
  delivery: AlertDelivery,
  now = Date.now(),
): void {
  useAlertsStore.setState((s) => {
    if (s.seen.includes(alert.id)) return s;
    const seen = [...s.seen, alert.id].slice(-SEEN_CAP);
    if (delivery === 'none') return { seen };
    const entries = [{ alert, unread: delivery !== 'fold' }, ...s.entries]
      .sort((x, y) => atOf(y.alert) - atOf(x.alert))
      .slice(0, LIST_CAP);
    const next: Partial<IAlertsState> = {
      seen,
      entries,
      lastSeenAt: later(s.lastSeenAt, alert.at),
    };
    if (delivery === 'queue') next.awayCount = s.awayCount + 1;
    // The drop does not take focus: the polite live region speaks it, as the quiet ones.
    if (delivery === 'quiet' || delivery === 'fold' || delivery === 'drop') next.said = alert.id;
    // An open summary already lists every unread alert: the news joins it, no 2nd card.
    if (delivery === 'dialog' && !s.summary) {
      const d = s.dialog;
      next.dialog = d ? { alerts: grown(d, alert, now), at: d.at } : { alerts: [alert], at: now };
    }
    if (delivery === 'drop' && !s.summary) {
      const d = s.drop;
      next.drop = d ? { alerts: grown(d, alert, now), at: d.at } : { alerts: [alert], at: now };
    }
    return next;
  });
}

/** Everything at or before `upTo` is read (this tab's acknowledgement, or another's). */
export function markRead(upTo: string): void {
  useAlertsStore.setState((s) => {
    const cursor = Date.parse(upTo);
    const dialogDone = s.dialog?.alerts.every((a) => atOf(a) <= cursor);
    const dropDone = s.drop?.alerts.every((a) => atOf(a) <= cursor);
    const entries = s.entries.map((e) =>
      e.unread && atOf(e.alert) <= cursor ? { ...e, unread: false } : e,
    );
    return {
      readUpTo: later(s.readUpTo, upTo),
      entries,
      // Read elsewhere (another tab, another device): nothing left to summarise.
      ...(entries.some((e) => e.unread) ? {} : { awayCount: 0 }),
      ...(dialogDone ? { dialog: null } : {}),
      ...(dropDone ? { drop: null } : {}),
    };
  });
}

/** Selectors — primitives or existing references only (zustand v5 snapshot rule). */
export const unreadCount = (s: IAlertsState): number => s.entries.filter((e) => e.unread).length;
export const firstUnread = (s: IAlertsState): IOperatorAlertV1 | undefined =>
  s.entries.find((e) => e.unread)?.alert;

export function openSummary(reason: 'return' | 'list'): void {
  useAlertsStore.setState({ summary: reason, dialog: null, drop: null });
}

/**
 * The drop leaves by itself (its 10 s ran out): it closes and acknowledges nothing — the
 * news stays unread on the bell until the operator looks at it.
 */
export function dismissDrop(): void {
  useAlertsStore.setState({ drop: null });
}

/* ── the host's runtime: what the policy needs at event time, and the side effects ── */

export interface IAlertsRuntime {
  /** The live context (presence, focus, leader, me) — read at event time. */
  context(): Omit<IDeliveryContext, 'now' | 'read'>;
  /** An acknowledgement moved the cursor: tell the BFF and the other tabs. */
  acknowledged(upTo: string): void;
}

let runtime: IAlertsRuntime | null = null;

/** The alert host (components/alerts/alerts-host.tsx) registers itself while mounted. */
export function setAlertsRuntime(next: IAlertsRuntime | null): void {
  runtime = next;
}

/**
 * One alert, live or caught up. Without a mounted host (a screen outside the console
 * shell) nothing interrupts: what the policy keeps goes to the list only.
 */
export function receiveAlert(alert: IOperatorAlertV1, over: Partial<IDeliveryContext> = {}): void {
  const base = runtime?.context() ?? { away: false, focus: null, leader: false };
  const delivery = deliveryOf(alert, {
    ...base,
    now: Date.now(),
    read: useAlertsStore.getState().readUpTo,
    ...over,
  });
  takeAlert(alert, runtime || delivery === 'none' ? delivery : 'inbox');
}

/**
 * Close the dialog / summary and acknowledge what it showed: the cursor moves to the
 * newest of them, here, on the BFF (`POST /v1/alerts/read`) and in the other tabs.
 */
export function acknowledge(alerts: readonly IOperatorAlertV1[]): void {
  useAlertsStore.setState({ dialog: null, drop: null, summary: null, awayCount: 0 });
  const upTo = upToOf(alerts);
  if (!upTo) return;
  markRead(upTo);
  runtime?.acknowledged(upTo);
}

/**
 * ONE catch-up read (reconnect / return): merged through the same policy and dedupe.
 * `awaySince` — on a return, what happened since then is the summary's. A read already
 * in flight covers this one (never two at once).
 */
export async function catchUp(awaySince?: number, qc: QueryClient = queryClient): Promise<void> {
  const { sync, lastSeenAt: since } = useAlertsStore.getState();
  if (sync === 'loading') return;
  useAlertsStore.setState({ sync: 'loading' });
  try {
    const page = await qc.fetchQuery({
      queryKey: ['alerts', 'since', since],
      queryFn: () => getAlerts(since),
      staleTime: 0,
      gcTime: 0,
      // ONE read per trigger: a failure shows the summary's error + «Reintentar».
      retry: false,
    });
    if (page.readUpTo) markRead(page.readUpTo);
    const over = awaySince === undefined ? {} : { away: true, awaySince };
    for (const alert of page.items ?? []) if (isOperatorAlertV1(alert)) receiveAlert(alert, over);
    useAlertsStore.setState({ sync: 'idle' });
  } catch {
    useAlertsStore.setState({ sync: 'error' });
  }
}
